/**
 * Shared GLSL building blocks.
 *
 * Everything here is procedural. The project ships no texture assets for
 * terrain: surface detail comes from hash noise evaluated in the fragment
 * shader and modulated by the per-vertex biome, slope and humidity attributes.
 * That keeps the download small, removes any possibility of visible tiling at
 * planetary scale, and lets detail adapt continuously to camera distance.
 */

/**
 * Formats a TypeScript number for interpolation into a `float` context in a
 * GLSL template string.
 *
 * `` `${3.0}` `` stringifies to `"3"` — a bare integer literal — because JS
 * drops a trailing `.0`. GLSL ES has no implicit int→float conversion, so
 * `const float X = 3;` is a type error on a real driver (SwiftShader accepts
 * it, which is exactly the class of divergence this project has been burned
 * by before). Route any interpolated float constant through this so the
 * emitted literal always has a decimal point.
 */
export function glslFloat(n: number): string {
  return Number.isInteger(n) ? `${n}.0` : `${n}`;
}

/**
 * Constants shared verbatim across shaders. Anything that appears identically
 * in more than one material belongs here rather than being re-declared (and
 * risking re-tuned, since a copy invites drift) at each call site.
 *
 * Every consumer of `GLSL_LIGHTING` or `GLSL_ATMOSPHERE` must include this
 * before them — both reference `PI`.
 */
export const GLSL_CONSTANTS = /* glsl */ `
// Full double-precision-derived PI. The extra digits over the common 3.14159
// rounding matter here only in that every shader should agree on exactly the
// same value; using a shorter literal in one place and a longer one in
// another is a needless (if tiny) source of cross-shader inconsistency.
const float PI = 3.14159265358979;

// Aerial-perspective in-scatter tuning, shared by TerrainMaterial's ground fog
// and CloudLayer's own deck haze — both fade a sunlit air column in across
// roughly the same twilight band and floor it at roughly the same multiple-
// scattering fraction, so the ground and the clouds above it dim together
// rather than at two independently-tuned rates.
//
// AERIAL_MULTI_SCATTER_FLOOR stands in for multiple scattering, which is not
// modelled: even on the night side a hazy sky stays faintly luminous from
// light that has bounced more than once, so in-scatter never reaches zero.
// AERIAL_INSCATTER_SUNUP_WEIGHT is the additional in-scatter while the sun is
// up, on top of that floor.
const float AERIAL_MULTI_SCATTER_FLOOR = 0.18;
const float AERIAL_INSCATTER_SUNUP_WEIGHT = 0.75;
// dot(up, sunDirection) range across which in-scatter ramps from the night
// floor to full daytime strength. Deliberately wider than a settlement-light
// terminator band: airglow and haze should still be fading out well after
// surface lights have come up, not snap off with them.
const float AERIAL_SUNLIT_AIR_START = -0.25;
const float AERIAL_SUNLIT_AIR_END = 0.15;
// Matches atmosphereDefaults.MIN_SCALE_HEIGHT: uScaleHeight is already
// floored to that value when the uniform is set, so this is a defensive
// floor against a stale or otherwise unfloored value reaching the density
// exponent's denominator, not an independent tuning knob.
const float AERIAL_SCALE_HEIGHT_FLOOR = 1.0;

// Twilight reddening for the aerial-perspective in-scatter shared by
// TerrainMaterial's ground fog and CloudLayer's deck haze. As the star drops
// toward the horizon its direct beam has already crossed the atmosphere
// edge-on and had the blue scattered out of it, so the light the air column
// between camera and surface scatters is warm, not the body's daytime haze
// hue. The ray-marched shell (AtmosphereMaterial) integrates this properly on
// the horizon; without an equivalent here the ground stayed veiled in blue
// while the horizon behind it burned red at sunrise and sunset.
//
// Rather than a fixed colour, the fog is dimmed and reddened by a closed-form
// estimate of the sun beam's own optical depth, so it tracks the shell per
// world and per elevation instead of being tuned to one planet. The beam
// crosses ~1/sin(elevation) scale heights of air (the plane-parallel airmass,
// floored so it stays finite at the horizon) and the Rayleigh component
// attenuates the blue end of that beam first, exactly the reddening the shell
// marches. The transmission is expressed relative to the zenith sun
// (airmass - 1.0), so daytime fog keeps its normal colour and the shift only
// appears as the star drops. The beam's optical depth scales the vertical
// column (density times scale height) by the airmass; the density is clamped
// the same way AtmosphereMaterial's uDensityScale is, so an outlier world like
// Ashkar's 58 kg/m3 soup can't drive the whole fog to black.
//
// AERIAL_MIE_OPTICAL_DEPTH_GAIN matches AtmosphereMaterial's
// MIE_OPTICAL_DEPTH_GAIN so the two surfaces agree on how much the (colourless)
// Mie term contributes to the beam's extinction.
const float AERIAL_MIE_OPTICAL_DEPTH_GAIN = 1.1;
const float AERIAL_SUN_BEAM_SIN_FLOOR = 0.02;  // ~1.1 deg sun elevation: below this the beam is fully reddened
// Matches atmosphereDefaults.DENSITY_SCALE_*: the clamped surface density the
// vertical column of the beam is scaled by.
const float AERIAL_DENSITY_SCALE_NOMINAL = 1.2;
const float AERIAL_DENSITY_SCALE_MIN = 0.25;
const float AERIAL_DENSITY_SCALE_MAX = 3.0;

// Night-side emissive patch shaping, shared by TerrainMaterial's settlement
// lights and AstronomicalLayer's distant-impostor equivalent — both want the
// same "sparse, clustered patches, not a smooth wash" look (see either
// file's own comment for the full reasoning) so a body does not visibly
// change how its night side looks the moment it hands off between the two
// representations.
//
// NIGHT_GLOW_PRIMARY_WEIGHT/SECONDARY_WEIGHT blend a coarse and a fine fbm
// into one glow field. NIGHT_GLOW_CLUSTER_START/END threshold that field high
// and narrow so only its brightest few percent lights at all; cubing the
// result (done at each call site, not here) sharpens the falloff further.
// NIGHT_GLOW_GRAIN_START/END threshold a second, finer field to break the
// surviving clusters into discrete patches instead of one smooth blob.
const float NIGHT_GLOW_PRIMARY_WEIGHT = 0.65;
const float NIGHT_GLOW_SECONDARY_WEIGHT = 0.35;
const float NIGHT_GLOW_CLUSTER_START = 0.76;
const float NIGHT_GLOW_CLUSTER_END = 0.86;
const float NIGHT_GLOW_GRAIN_START = 0.55;
const float NIGHT_GLOW_GRAIN_END = 0.85;
`;

/**
 * Saturn-like radial optical-depth profile shared by the visible ring and by
 * every planetary material that receives its shadow. Keeping this in one
 * function makes the Cassini and Encke gaps line up with the light passing
 * through them instead of casting a generic solid annulus.
 */
export const GLSL_RING_DENSITY = /* glsl */ `
const float RING_C_REGION_END = 0.24;
const float RING_B_REGION_END = 0.56;
const float RING_CASSINI_GAP_START = 0.565;
const float RING_CASSINI_GAP_END = 0.635;
const float RING_ENCKE_GAP_CENTRE = 0.825;
const float RING_ENCKE_GAP_WIDTH = 0.006;
const float RING_FINE_COUNT = 420.0;
const float RING_SHADOW_MIN_FILTER = 0.006;

float ringHash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}

float ringInterval(float x, float lo, float hi, float aa) {
  return smoothstep(lo - aa, lo + aa, x)
    * (1.0 - smoothstep(hi - aa, hi + aa, x));
}

float ringDensityProfile(float radial, float aa, out float broadOut) {
  radial = clamp(radial, 0.0, 1.0);
  float cRegion = 0.48 + 0.52 * smoothstep(0.0, RING_C_REGION_END, radial);
  float bRegion = 1.0 - 0.24 * smoothstep(RING_B_REGION_END - 0.08, RING_B_REGION_END, radial);
  broadOut = 0.72
    + 0.16 * sin(2.0 * PI * (radial * 7.0 + 0.13))
    + 0.08 * sin(2.0 * PI * (radial * 19.0 - 0.31));

  float cell = floor(radial * RING_FINE_COUNT);
  float cellNoise = ringHash11(cell + 17.0);
  float cellWidth = 1.0 / RING_FINE_COUNT;
  float fineVisibility = 1.0 - smoothstep(cellWidth * 0.45, cellWidth * 2.4, aa);
  float fine = mix(1.0, mix(0.52, 1.28, cellNoise), fineVisibility);

  float cassini = 1.0 - ringInterval(
    radial,
    RING_CASSINI_GAP_START,
    RING_CASSINI_GAP_END,
    aa
  ) * 0.94;
  float enckeWidth = max(RING_ENCKE_GAP_WIDTH, aa * 1.5);
  float encke = 1.0 - exp(-pow((radial - RING_ENCKE_GAP_CENTRE) / enckeWidth, 2.0)) * 0.82;
  float secondaryGap = 1.0 - exp(-pow((radial - 0.315) / max(0.003, aa), 2.0)) * 0.46;
  float edge = smoothstep(0.0, max(0.018, aa * 2.0), radial)
    * (1.0 - smoothstep(1.0 - max(0.014, aa * 2.0), 1.0, radial));

  return clamp(cRegion * bRegion * broadOut * fine, 0.05, 1.25)
    * cassini * encke * secondaryGap * edge;
}

float ringShadowAtPoint(
  vec3 point,
  vec3 sunDirection,
  vec3 ringNormal,
  float innerRadius,
  float outerRadius,
  float opacity
) {
  if (opacity <= 0.0 || outerRadius <= innerRadius) return 0.0;
  vec3 L = normalize(sunDirection);
  vec3 N = normalize(ringNormal);
  float sunPlane = dot(L, N);
  if (abs(sunPlane) < 0.0001) return 0.0;

  // Trace from the shaded point toward the star. Only an intersection in
  // front of the point can remove incoming light.
  float t = -dot(point, N) / sunPlane;
  if (t <= 0.0) return 0.0;
  vec3 ringHit = point + L * t;
  float hitRadius = length(ringHit);
  float radial = (hitRadius - innerRadius) / max(outerRadius - innerRadius, 1e-5);
  if (radial <= 0.0 || radial >= 1.0) return 0.0;

  // A curved receiving surface cannot resolve hundreds of razor-thin
  // ringlets without turning them into dotted arcs. Use an explicit optical
  // footprint here: derivatives inside the divergent ray/plane intersection
  // above are undefined for neighbouring fragments that miss the ring and can
  // themselves produce dashed arcs. The visible ring keeps its finer,
  // derivative-driven filter.
  float aa = RING_SHADOW_MIN_FILTER;
  float broad;
  float density = ringDensityProfile(radial, aa, broad);
  // A shallow ray crosses more particles. Beer-Lambert transmission preserves
  // partially transparent bands and naturally deepens an edge-on shadow.
  float opticalDepth = opacity * density / max(abs(sunPlane), 0.04);
  return 1.0 - exp(-opticalDepth);
}
`;

export const GLSL_NOISE = /* glsl */ `
// --- Hash-based value noise -------------------------------------------------
// Matches the CPU noise in spirit but not in exact values; it is only used for
// sub-metre visual detail, never for anything the simulation depends on.
float hash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}

float hash13(vec3 p3) {
  p3 = fract(p3 * 0.1031);
  p3 += dot(p3, p3.zyx + 31.32);
  return fract((p3.x + p3.y) * p3.z);
}

vec3 hash33(vec3 p3) {
  p3 = fract(p3 * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yxz + 33.33);
  return fract((p3.xxy + p3.yxx) * p3.zyx);
}

float valueNoise(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  float n000 = hash13(i + vec3(0.0, 0.0, 0.0));
  float n100 = hash13(i + vec3(1.0, 0.0, 0.0));
  float n010 = hash13(i + vec3(0.0, 1.0, 0.0));
  float n110 = hash13(i + vec3(1.0, 1.0, 0.0));
  float n001 = hash13(i + vec3(0.0, 0.0, 1.0));
  float n101 = hash13(i + vec3(1.0, 0.0, 1.0));
  float n011 = hash13(i + vec3(0.0, 1.0, 1.0));
  float n111 = hash13(i + vec3(1.0, 1.0, 1.0));
  return mix(
    mix(mix(n000, n100, f.x), mix(n010, n110, f.x), f.y),
    mix(mix(n001, n101, f.x), mix(n011, n111, f.x), f.y),
    f.z);
}

float fbm(vec3 p, int octaves) {
  float sum = 0.0;
  float amp = 0.5;
  float norm = 0.0;
  for (int i = 0; i < 8; i++) {
    if (i >= octaves) break;
    sum += valueNoise(p) * amp;
    norm += amp;
    p *= 2.03;
    amp *= 0.5;
  }
  return norm > 0.0 ? sum / norm : 0.0;
}

// Worley F1, used for cracks, pebbles and crystal facets.
//
// A single cubic lattice of jittered points reads as a visible axis-aligned
// grid even at full jitter: the lattice's neighbour structure is still just
// 6 axis-aligned cells, so most of F1's nearest points line up along X/Y/Z and
// the cell boundaries come out as near-straight streets — the same failure
// documented for the old plate-boundary lattice in TerrainPipeline.ts (see
// buildPlatePoints), reported there as "a hexagon-like grid baked into
// distant terrain". That fix could afford to precompute an irregular point
// set once per planet; this one runs per-fragment on close terrain and has no
// equivalent budget for a global point set.
//
// A body-centred-cubic lattice — this cubic grid plus a second one offset by
// half a cell in every axis, keeping the nearer of the two hits — has 14
// nearest-neighbour directions instead of 6, which is enough to break the
// axis bias without a second full-precision search: it is the standard fix
// for this exact look in cellular noise.
float worleyLattice(vec3 p, vec3 offset) {
  vec3 i = floor(p - offset);
  vec3 f = p - offset - i;
  float best = 1e9;
  for (int z = -1; z <= 1; z++) {
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec3 g = vec3(float(x), float(y), float(z));
        vec3 o = hash33(i + g + offset * 7.0);
        float d = length(g + o - f);
        best = min(best, d);
      }
    }
  }
  return best;
}

float worley(vec3 p) {
  float a = worleyLattice(p, vec3(0.0));
  float b = worleyLattice(p, vec3(0.5));
  return min(a, b);
}
`;

/**
 * The cloud deck's density field, shared between the deck and its shadow.
 *
 * This lives here rather than in CloudLayer because two different shaders have
 * to agree on it exactly. The deck draws it; the terrain samples it along the
 * sun direction to know what is shadowed. Reimplementing the field on the
 * terrain side would guarantee the shadows drift out of register with the cloud
 * casting them, and a shadow that does not line up with its cloud is worse than
 * no shadow at all — it reads as dirt on the ground.
 *
 * `local` is a NORMALISED planet-fixed direction. Renormalising matters: a unit
 * vector interpolated across a triangle is shorter in the middle than at the
 * corners, so the raw varying samples the noise at a slightly different scale
 * inside every quad and draws the mesh's own tessellation across the deck.
 *
 * `detailOut` returns the fine octave, which the deck needs for opacity and the
 * shadow does not.
 */
export const GLSL_CLOUD_FIELD = /* glsl */ `
float cloudDensityAt(
  vec3 local,
  float time,
  float drift,
  float coverage,
  float banded,
  out float detailOut
) {
  // Planet-fixed direction, slowly drifting: weather moves relative to the
  // ground rather than the ground moving under a fixed pattern.
  // drift is the authored wind's angular speed (radians/second). The field
  // has three cells per radian, so applying the same factor here advects the
  // macro weather at the configured surface speed instead of at an arbitrary
  // shader-tuned multiple of it.
  vec3 p = local * 3.0 + vec3(time * drift * 3.0, 0.0, time * drift * 1.8);

  // Two scales of fbm with a domain warp. The warp is what turns bland blobs
  // into fronts and streaks with a direction to them. Softened from 1.4: at
  // near-total coverage that single streak direction had nothing else in the
  // field to compete with it and read as combed lines rather than clouds.
  vec3 warp = vec3(
    fbm(p * 1.7, 3),
    fbm(p * 1.7 + vec3(17.0), 3),
    fbm(p * 1.7 + vec3(43.0), 3)
  ) - 0.5;
  vec3 q = p + warp * 0.55;

  // A low-frequency synoptic map decides where weather systems exist on the
  // globe. Without this scale the broad and mesoscale octaves cover the whole
  // sphere with equally-sized curls: detailed up close, but a uniform marble
  // from orbit. Its additive bias opens genuinely large clear regions and
  // gathers the remaining clouds into continental-scale fronts.
  float synoptic = fbm(q * 0.38 + vec3(31.7, -18.2, 5.4), 5);
  float broad = fbm(q * 0.92, 5);
  // Mesoscale fronts carve cells and trailing filaments inside those weather
  // systems; they refine a group but are not allowed to create one by
  // themselves in a synoptically clear region.
  float mesoscale = fbm(q * 2.7 + vec3(9.7, -4.3, 13.1), 4);
  detailOut = fbm(q * 7.2 + vec3(-11.4, 6.8, 3.2), 4);

  // Gas giants band: latitude dominates the pattern instead of the noise.
  float bands = 0.5 + 0.5 * sin(local.y * 22.0 + fbm(q * 2.0, 3) * 3.0);

  // Cloud/clear ownership comes from all three global scales. Synoptic is a
  // bias rather than another average: averaging fbm octaves narrows their
  // range and recreates the same middle-grey coverage everywhere. The finest
  // detail stays out of this threshold so silhouettes remain coherent.
  float weather = mix(broad, mesoscale, 0.24) + (synoptic - 0.46) * 0.70;
  float banded_ = mix(weather, bands * 0.72 + weather * 0.28, banded);
  // A second, independent layer of noise with the axes cycled (so its own
  // warp-driven streaks run a different way) blended in at low weight. Breaks
  // up the single dominant streak direction the warp alone produces, without
  // erasing it — the field still reads as fronts, just no longer as combed
  // parallel lines. Must match globalCloudDensityAt's CPU twin in CloudLayer.ts.
  float crossGrain = fbm(q.zxy * 1.35 + vec3(51.0, 7.0, 22.0), 4);
  float field = mix(banded_, crossGrain, 0.4);

  // Coverage remains a threshold, but the old half-unit transition compressed
  // most of the planet into the same milky mid-opacity. A narrower soft band
  // produces clear gaps and dense cores. Fine detail modulates opacity only
  // after that decision, preserving coherent fronts while exposing texture.
  float threshold = 1.0 - coverage;
  float density = smoothstep(threshold - 0.065, threshold + 0.085, field);
  float opacityDetail = mix(0.62, 1.28, detailOut);
  return clamp(pow(density, 1.20) * opacityDetail, 0.0, 1.0);
}
`;

/**
 * Fine-scale cloud detail for the near-field march in CloudLayer, sampled in
 * real METRES rather than the angular unit-sphere coordinate `cloudDensityAt`
 * uses.
 *
 * That distinction is the whole point. The deck sits only a few kilometres
 * above the ground while its shell spans a planetary radius of several
 * thousand kilometres, so the parallax between looking straight up and
 * looking toward the horizon is a small fraction of a degree — the entire
 * visible sky dome samples `cloudDensityAt` at very nearly one point, which is
 * why it read as a flat, textureless wash rather than clouds. A grazing ray
 * still travels real kilometres through the deck even though it barely moves
 * angularly, so keying detail to metres of travel (not degrees) is what
 * recovers texture at ground level. This is deliberately cheap — one 3-octave
 * fbm — because CloudLayer calls it once per march step.
 */
export const GLSL_CLOUD_DETAIL = /* glsl */ `
float cloudDetailAt(vec3 posMetres, float time, float drift, float planetRadius) {
  // The base lobe is deliberately several kilometres wide. At 3.2 km the
  // march produced a carpet of small cells; a broader 7.2 km lobe lets the
  // shared weather mass resolve into recognisable cumulus bodies. Fine erosion
  // is supplied separately by CloudLayer, so this field need not spend its
  // limited octaves recreating it.
  float windMetres = drift * planetRadius * time;
  vec3 p = (posMetres + vec3(windMetres, 0.0, windMetres * 0.6)) * (1.0 / 7200.0);
  return fbm(p, 4);
}
`;

export const GLSL_LIGHTING = /* glsl */ `
// --- Physically inspired direct lighting ------------------------------------
// A single stellar light source with GGX specular and a hemispheric ambient
// term standing in for sky and bounce light. No arbitrary fill lights: night
// sides are genuinely dark, which is the point.

float distributionGGX(vec3 N, vec3 H, float roughness) {
  float a = roughness * roughness;
  float a2 = a * a;
  float NdotH = max(dot(N, H), 0.0);
  float d = NdotH * NdotH * (a2 - 1.0) + 1.0;
  return a2 / max(PI * d * d, 1e-7);
}

float geometrySchlick(float NdotV, float roughness) {
  float r = roughness + 1.0;
  float k = (r * r) / 8.0;
  return NdotV / (NdotV * (1.0 - k) + k);
}

float geometrySmith(vec3 N, vec3 V, vec3 L, float roughness) {
  return geometrySchlick(max(dot(N, V), 0.0), roughness) * geometrySchlick(max(dot(N, L), 0.0), roughness);
}

vec3 fresnelSchlick(float cosTheta, vec3 F0) {
  return F0 + (1.0 - F0) * pow(clamp(1.0 - cosTheta, 0.0, 1.0), 5.0);
}

vec3 shadeSurface(
  vec3 albedo,
  vec3 N,
  vec3 V,
  vec3 L,
  vec3 lightColor,
  float lightIntensity,
  float roughness,
  float metalness,
  vec3 ambientSky,
  vec3 ambientGround,
  vec3 up
) {
  vec3 H = normalize(V + L);
  float NdotL = max(dot(N, L), 0.0);

  vec3 F0 = mix(vec3(0.04), albedo, metalness);
  float NDF = distributionGGX(N, H, roughness);
  float G = geometrySmith(N, V, L, roughness);
  vec3 F = fresnelSchlick(max(dot(H, V), 0.0), F0);

  vec3 numerator = NDF * G * F;
  float denominator = 4.0 * max(dot(N, V), 0.0) * NdotL + 1e-4;
  vec3 specular = numerator / denominator;

  vec3 kD = (vec3(1.0) - F) * (1.0 - metalness);
  vec3 direct = (kD * albedo / PI + specular) * lightColor * lightIntensity * NdotL;

  // Hemispheric ambient: sky above, bounced ground light below. "Up" is the
  // local radial direction, not the world Y axis. Using Y here means the
  // ambient term is only correct near one pole and gradually inverts as you
  // travel away from it, which on a sphere is most of the surface.
  float hemi = dot(N, up) * 0.5 + 0.5;
  vec3 ambient = mix(ambientGround, ambientSky, hemi) * albedo;

  return direct + ambient;
}
`;

export const GLSL_ATMOSPHERE = /* glsl */ `
// --- Atmospheric scattering -------------------------------------------------
// Single-scattering Rayleigh + Mie along a view ray, with optical depth
// integrated toward the star. This is the standard Nishita-style formulation,
// truncated to a step count chosen by the quality profile.

// Returns (near, far) intersection distances, or (-1, -1) when the ray misses.
/**
 * Ray-sphere intersection, written to survive planetary radii in 32-bit float.
 *
 * The textbook form computes c = dot(o,o) - r*r. At planetary scale both terms
 * are around 3.7e13 while their difference — which is all that matters — is the
 * altitude times the diameter, some seven orders of magnitude smaller. A 32-bit
 * float has about seven significant digits, so that subtraction destroys every
 * one of them and the returned intersections are noise.
 *
 * The factored form (|o| - r)(|o| + r) is algebraically identical and
 * numerically completely different: the first factor is the altitude, computed
 * directly and accurately, and nothing large is ever subtracted from anything
 * large. This is the same failure mode as the reserved-word bug — invisible on
 * a software rasteriser that computes at wider precision, fatal on real
 * hardware — and it is why the atmosphere shell rendered here and vanished on a
 * GPU.
 */
vec2 raySphere(vec3 origin, vec3 dir, float radius) {
  float b = dot(origin, dir);
  float len = length(origin);
  float c = (len - radius) * (len + radius);
  float d = b * b - c;
  if (d < 0.0) return vec2(-1.0, -1.0);
  float sqrtD = sqrt(d);
  // Avoid cancellation in the near root when the observer is only metres or
  // kilometres from a sphere whose radius is measured in millions of metres.
  // q is always the root whose two terms reinforce; Vieta recovers the other.
  float q = -b - (b >= 0.0 ? sqrtD : -sqrtD);
  if (abs(q) < 1e-6) return vec2(-b - sqrtD, -b + sqrtD);
  float root0 = q;
  float root1 = c / q;
  return vec2(min(root0, root1), max(root0, root1));
}

float rayleighPhase(float cosTheta) {
  return (3.0 / (16.0 * PI)) * (1.0 + cosTheta * cosTheta);
}

float miePhase(float cosTheta, float g) {
  float g2 = g * g;
  float denom = 1.0 + g2 - 2.0 * g * cosTheta;
  return (3.0 / (8.0 * PI)) * ((1.0 - g2) * (1.0 + cosTheta * cosTheta)) /
         ((2.0 + g2) * max(pow(denom, 1.5), 1e-4));
}

float atmosphereDensity(float height, float scaleHeight) {
  return exp(-max(height, 0.0) / scaleHeight);
}
`;

/** Filmic tone mapping. ACES-inspired, fitted for a fast approximation. */
export const GLSL_TONEMAP = /* glsl */ `
vec3 acesTonemap(vec3 x) {
  const float a = 2.51;
  const float b = 0.03;
  const float c = 2.43;
  const float d = 0.59;
  const float e = 0.14;
  return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
}

vec3 linearToSRGB(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(1e-5)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}
`;

/**
 * Screen-space dither, for cross-fading an OPAQUE surface against whatever is
 * already in the colour buffer behind it.
 *
 * The renderer clears depth between passes but never colour (`autoClear` is
 * false — see `LayeredRenderer.render`), and the astronomical pass that draws a
 * body's distant impostor runs *before* the world passes that draw its real
 * terrain. So a terrain fragment that discards leaves the impostor's own pixel
 * standing, and discarding a growing fraction of them cross-fades the two
 * representations with no transparency, no sorting, and no second draw.
 *
 * Alpha blending cannot do this job here: terrain chunks meet at skirts that
 * overlap, and a blended overlap composites twice into a visible seam. A
 * stochastic threshold has no such failure — each pixel belongs to exactly one
 * representation.
 *
 * Interleaved gradient noise rather than an ordered Bayer matrix: at this
 * screen size a 4x4 Bayer cell reads as a visible grid crawling across the
 * terminator, while IGN is cheap, well distributed, and reads as fine grain
 * that the eye integrates over the half-second the fade lasts.
 */
export const GLSL_DITHER = /* glsl */ `
float interleavedGradientNoise(vec2 fragCoord) {
  return fract(52.9829189 * fract(0.06711056 * fragCoord.x + 0.00583715 * fragCoord.y));
}

/**
 * True when this fragment belongs to the representation being faded OUT and
 * should therefore be dropped. 'fade' is 1 for fully present, 0 for absent.
 */
bool ditheredOut(float fade, vec2 fragCoord) {
  return fade < 0.999 && interleavedGradientNoise(fragCoord) > fade;
}
`;
